import CDS from "design-system";
import type { UserRole } from "features/User/models";
import { roleStyles } from "./helpers";

const ROLE_LABEL: Record<string, string> = {
  new_hire: "New hire",
  manager: "Manager",
  buddy: "Buddy",
  people_team_contact: "People Team",
  recruiter: "Recruiter",
  it_team_contact: "IT",
};

interface UserShape {
  fullName: string;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
}

function userInitials(user: UserShape): string {
  return user.fullName.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("");
}

interface Props {
  role?: string | null | undefined;
  /** When provided, renders a specific-user pill instead of a role pill. */
  user?: UserShape | null;
  size?: "xs" | "sm" | "md";
  emptyLabel?: string;
  /** Render only the colored avatar circle, no label text. Used in compact density mode. */
  iconOnly?: boolean;
  /** When provided, renders a remove (x) button that fires this callback. */
  remove?: () => void;
  /** Adds a 1px border in the chip's text color — for use on tinted surfaces. */
  bordered?: boolean;
}

/**
 * Role-or-user chip for the task grid row axis, message recipient, and any
 * other surface that shows "who is this assigned to / addressed to". Thin
 * adapter that maps role/user/empty cases to the shared CDS.RecipientChip
 * primitive — all chip styling lives in the design system now.
 */
export default function RoleLabelTag({
  role,
  user,
  size = "sm",
  emptyLabel = "—",
  iconOnly = false,
  remove,
  bordered = false,
}: Props) {
  if (user) {
    return (
      <CDS.RecipientChip
        variant="user"
        label={user.fullName}
        avatarUrl={user.avatarUrl ?? undefined}
        initials={userInitials(user)}
        size={size}
        bordered={bordered}
        remove={remove}
      />
    );
  }

  if (!role) {
    return <CDS.RecipientChip variant="empty" label={emptyLabel} size={size} />;
  }

  const styles = roleStyles(role as UserRole);
  return (
    <CDS.RecipientChip
      variant="role"
      label={ROLE_LABEL[role] ?? role}
      roleColors={{ bg: styles.light, fg: styles.dark }}
      size={size}
      bordered={bordered}
      remove={remove}
      iconOnly={iconOnly}
    />
  );
}
